import { useEffect } from 'react';
import { useNotificationStore } from '../../stores/notificationStore';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { CheckCheck, Bell, BellOff } from 'lucide-react';
import { formatDate } from '../../lib/utils';

export default function Notifications() {
  const { notifications, isLoading, fetchNotifications, markAsRead, markAllAsRead } = useNotificationStore();

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const unreadNotifications = notifications.filter((n) => !n.isRead);
  const readNotifications = notifications.filter((n) => n.isRead);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-lg">Loading notifications...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[2.25rem] font-semibold tracking-tightest text-glass-text">Notifications</h1>
          <p className="text-sm text-glass-text-2 mt-1">
            {unreadNotifications.length > 0
              ? `You have ${unreadNotifications.length} unread notification${unreadNotifications.length !== 1 ? 's' : ''}`
              : 'All caught up!'}
          </p>
        </div>
        {unreadNotifications.length > 0 && (
          <Button onClick={markAllAsRead} variant="outline" className="flex items-center gap-2">
            <CheckCheck className="w-4 h-4" />
            Mark All as Read
          </Button>
        )}
      </div>

      {notifications.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <BellOff className="w-16 h-16 text-glass-text-3 mb-4" />
            <p className="text-glass-text-2 text-lg">No notifications yet</p>
            <p className="text-glass-text-3 text-sm mt-2">
              You'll see notifications here when there's activity on your account
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Unread Notifications */}
          {unreadNotifications.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-lg font-semibold text-glass-text flex items-center gap-2">
                <Bell className="w-5 h-5 text-accent2" />
                Unread ({unreadNotifications.length})
              </h2>
              {unreadNotifications.map((notification) => (
                <Card
                  key={notification.id}
                  className="border-l-4 !border-l-accent2 bg-accent2/10 hover:bg-accent2/15 transition-colors cursor-pointer"
                  onClick={() => markAsRead(notification.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <p className="text-glass-text font-medium">{notification.title}</p>
                        {notification.body && (
                          <p className="text-glass-text-2 text-sm mt-1">{notification.body}</p>
                        )}
                        <p className="text-glass-text-3 text-xs mt-2">
                          {formatDate(notification.createdAt)}
                        </p>
                      </div>
                      <div className="w-2 h-2 bg-accent2 rounded-full flex-shrink-0 mt-2"></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Read Notifications */}
          {readNotifications.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-lg font-semibold text-glass-text flex items-center gap-2">
                <CheckCheck className="w-5 h-5 text-glass-text-3" />
                Read ({readNotifications.length})
              </h2>
              {readNotifications.map((notification) => (
                <Card key={notification.id} className="bg-glass-2">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <p className="text-glass-text-2">{notification.title}</p>
                        {notification.body && (
                          <p className="text-glass-text-3 text-sm mt-1">{notification.body}</p>
                        )}
                        <p className="text-glass-text-3 text-xs mt-2">
                          {formatDate(notification.createdAt)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

